'use client';

import { useCallback, useRef } from 'react';

import type { PartialKeys, VirtualizerOptions } from '@tanstack/react-virtual';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import type { OfferData } from '@wwe-portal/entities/offer.type';
import type { Banner } from '@wwe-portal/ui/features/offer';

import type { StoreItemVirtualRow } from '../virtual-row/virtual-row';

export interface StoreContentProps {
  content: StoreContentItemsProps[];
}
export interface StoreContentItemsProps {
  categoryId: string;
  categoryTitle: string;
  viewAs?: 'section' | 'text';
  offers?: OfferData[];
  sections?: {
    offers: OfferData[];
    banner: Banner | undefined;
    title: string;
    description: string;
    tooltipTitle?: string;
    sectionImage?: string;
  }[];
  banner: Banner | undefined;
}

export const useVirtualizeContent = ({
  categoryIndexes,
  estimatedSizes,
  rows,
  options,
}: {
  options?: PartialKeys<
    VirtualizerOptions<Window, Element>,
    | 'getScrollElement'
    | 'observeElementRect'
    | 'observeElementOffset'
    | 'scrollToFn'
    | 'count'
    | 'estimateSize'
    | 'scrollMargin'
  >;

  rows: StoreItemVirtualRow[];
  estimatedSizes: number[];
  categoryIndexes: Record<string, number>;
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  const estimateSize = useCallback(
    (index: number) => {
      return estimatedSizes[index] ?? 528;
    },
    [estimatedSizes]
  );
  const virtualizer = useWindowVirtualizer({
    ...options,
    count: rows.length,
    estimateSize,
    overscan: 5,
    scrollMargin: listRef.current?.offsetTop ?? 0,
  });

  return {
    listRef,
    rows,
    virtualizer,
    categoryIndexes,
  };
};
